import React, { useState } from 'react';
import { ChevronRight, ChevronLeft, Home, BookOpen, MessageCircle, Flower, Users, Sparkles, Image } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';

const FeatureTour = ({ onComplete }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      title: "Start your day with love",
      subtitle: "Home Tab 🏡",
      content: "See your affirmations, track your mood, and watch your Love Score grow as you take small daily actions.",
      icon: <Home className="w-20 h-20 text-rose-500" />,
      bgGradient: "from-rose-100 to-pink-100"
    },
    {
      title: "Write your way to clarity",
      subtitle: "Journaling ✍️",
      content: "Brain dump freely or use guided prompts. Journaling connects your thoughts to insights that move you forward.",
      icon: <BookOpen className="w-20 h-20 text-blue-500" />,
      bgGradient: "from-blue-100 to-cyan-100"
    },
    {
      title: "Your personal AI companion",
      subtitle: "Conchi 💖",
      content: "Chat with Conchi anytime. She'll reflect your patterns, ask deeper questions, and coach you using the NeuroLove Method.",
      icon: <MessageCircle className="w-20 h-20 text-purple-500" />,
      bgGradient: "from-purple-100 to-pink-100"
    },
    {
      title: "Play and grow",
      subtitle: "Love Garden 🌱",
      content: "A relaxing match-3 game where every win grows your Love Score. Fun, rewarding, and a reminder that growth can be joyful.",
      icon: <Flower className="w-20 h-20 text-green-500" />,
      bgGradient: "from-green-100 to-teal-100"
    },
    {
      title: "You're not alone",
      subtitle: "Community 💞",
      content: "Connect with women on the same journey. Celebrate wins, share struggles, and uplift each other.",
      icon: <Users className="w-20 h-20 text-orange-500" />,
      bgGradient: "from-orange-100 to-yellow-100"
    },
    {
      title: "Astrology + Scripture",
      subtitle: "Divine Guidance ✨",
      content: "Receive daily insights from the stars 🌙 or verses 📖 that speak directly to your journey. Save what resonates.",
      icon: <Sparkles className="w-20 h-20 text-indigo-500" />,
      bgGradient: "from-indigo-100 to-purple-100"
    },
    {
      title: "Picture your future",
      subtitle: "Vision Board 🌟",
      content: "Design a vision board with images and words that inspire you. Manifest the life you're stepping into.",
      icon: <Image className="w-20 h-20 text-pink-500" />,
      bgGradient: "from-pink-100 to-rose-100"
    }
  ];

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      onComplete();
    }
  };

  const handlePrev = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const currentSlideData = slides[currentSlide];
  const isLastSlide = currentSlide === slides.length - 1;

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className={`w-full max-w-2xl p-8 md:p-12 bg-gradient-to-br ${currentSlideData.bgGradient} border-2 border-rose-200 shadow-2xl`}>
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Explore Your Tools for Growth
          </h1>
          <p className="text-gray-600">
            Here's how Constant Love supports your journey
          </p>
        </div>

        {/* Progress Dots */}
        <div className="flex justify-center space-x-2 mb-8">
          {slides.map((_, index) => (
            <div
              key={index}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? 'w-8 bg-rose-600'
                  : index < currentSlide
                  ? 'w-2 bg-rose-400'
                  : 'w-2 bg-gray-300'
              }`}
            />
          ))}
        </div>

        {/* Icon */}
        <div className="flex justify-center mb-6">
          {currentSlideData.icon}
        </div>

        {/* Content */}
        <div className="text-center mb-8">
          <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">
            {currentSlideData.subtitle}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            {currentSlideData.title}
          </h2>
          <p className="text-lg md:text-xl text-gray-700 leading-relaxed max-w-xl mx-auto">
            {currentSlideData.content}
          </p>
        </div>

        {/* Final Slide Special Message */}
        {isLastSlide && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 mb-6 border-2 border-rose-200">
            <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">
              You're Ready! 💖
            </h3>
            <p className="text-gray-700 text-center">
              Dive in, explore, and remember — every small action adds up. Let's grow together.
            </p>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-12">
          <Button
            onClick={handlePrev}
            disabled={currentSlide === 0}
            variant="outline"
            className={`border-rose-300 text-rose-600 hover:bg-rose-50 ${
              currentSlide === 0 ? 'invisible' : ''
            }`}
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Back
          </Button>

          <div className="text-sm text-gray-600 font-medium">
            {currentSlide + 1} of {slides.length}
          </div>

          <Button
            onClick={handleNext}
            className="bg-rose-500 hover:bg-rose-600 text-white px-6"
          >
            {isLastSlide ? 'Go to My Dashboard ✨' : 'Next'}
            <ChevronRight className="w-5 h-5 ml-1" />
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default FeatureTour;